import React from 'react';
import {
  Calendar,
  Mail,
  Info,
  User,
  ChevronDown,
  ChevronUp,
  Phone,
  MessageCircle,
  Users,
  FileText,
  Paperclip,
  CheckSquare,
  Bell,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Note } from '../../../types/notes';
import type { Task } from '../../../types/tasks';

interface NoteCardProps {
  note: Note;
  isExpanded: boolean;
  onToggleExpand: () => void;
  /** The client's tasks, keyed by id. "3 follow-ups" is useless without their titles —
   *  the note only stores `task_ids`, so the titles have to come from the task spine. */
  tasksById?: Map<string, Task>;
}

const NoteCard: React.FC<NoteCardProps> = ({ note, isExpanded, onToggleExpand, tasksById }) => {
  // TASK-351: Extended type-specific configuration with new interaction types
  const typeConfig: Record<string, { icon: React.ReactNode; color: string; textColor: string; label: string }> = {
    meeting: {
      icon: <Calendar className="w-4 h-4" />,
      color: 'bg-blue-50 border-blue-200',
      textColor: 'text-blue-700',
      label: 'Meeting Note',
    },
    email: {
      icon: <Mail className="w-4 h-4" />,
      color: 'bg-purple-50 border-purple-200',
      textColor: 'text-purple-700',
      label: 'Email Summary',
    },
    system: {
      icon: <Info className="w-4 h-4" />,
      color: 'bg-gray-50 border-gray-200',
      textColor: 'text-gray-700',
      label: 'System Note',
    },
    advisor: {
      icon: <User className="w-4 h-4" />,
      color: 'bg-green-50 border-green-200',
      textColor: 'text-green-700',
      label: 'Advisor Note',
    },
    // TASK-351: New interaction types
    phone_call: {
      icon: <Phone className="w-4 h-4" />,
      color: 'bg-orange-50 border-orange-200',
      textColor: 'text-orange-700',
      label: 'Phone Call',
    },
    whatsapp: {
      icon: <MessageCircle className="w-4 h-4" />,
      color: 'bg-emerald-50 border-emerald-200',
      textColor: 'text-emerald-700',
      label: 'WhatsApp',
    },
    in_person: {
      icon: <Users className="w-4 h-4" />,
      color: 'bg-indigo-50 border-indigo-200',
      textColor: 'text-indigo-700',
      label: 'In-Person',
    },
    other: {
      icon: <FileText className="w-4 h-4" />,
      color: 'bg-slate-50 border-slate-200',
      textColor: 'text-slate-700',
      label: 'Note',
    },
  };

  // Fallback to 'other' for unknown types
  const config = typeConfig[note.type] || typeConfig.other;

  // TASK-351: Count linked entities for indicators
  const attachmentCount = note.attachment_ids?.length || 0;
  const taskCount = note.task_ids?.length || 0;
  // Only the tasks we can actually name. A task_id with no matching task (deleted, or
  // outside the fetched page) is left out rather than rendered as a blank row.
  const linkedTasks = (note.task_ids ?? [])
    .map((id) => tasksById?.get(id))
    .filter((t): t is Task => !!t);
  const hasReminder = !!note.reminder_id;
  const hasCalendarEvent = !!note.calendar_event_id;

  // Truncate summary if too long
  const maxLength = 200;
  const shouldTruncate = note.summary && note.summary.length > maxLength;
  const displaySummary = isExpanded || !shouldTruncate
    ? note.summary
    : note.summary?.substring(0, maxLength) + '...';

  // Format date
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  };

  // Format relative time
  const getRelativeTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <div className={`border rounded-lg p-4 ${config.color}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={config.textColor}>{config.icon}</span>
          <span className={`text-xs font-medium ${config.textColor}`}>{config.label}</span>
        </div>
        <div className="text-xs text-gray-600">
          {getRelativeTime(note.date)}
        </div>
      </div>

      {/* Date and Source */}
      <div className="text-xs text-gray-600 mb-2">
        {formatDate(note.date)} • {note.source}
      </div>

      {/* Summary/Content */}
      <p className="text-sm text-gray-800 mb-2 whitespace-pre-wrap">{displaySummary}</p>

      {/* Expand/Collapse Button */}
      {shouldTruncate && (
        <button
          onClick={onToggleExpand}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium mb-2"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="w-3 h-3" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              Read more
            </>
          )}
        </button>
      )}

      {/* Metadata (when expanded) */}
      {isExpanded && note.metadata && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          {note.metadata.topics && note.metadata.topics.length > 0 && (
            <div className="mb-2">
              <p className="text-xs font-medium text-gray-700 mb-1">Topics:</p>
              <div className="flex flex-wrap gap-1">
                {note.metadata.topics.map((topic, idx) => (
                  <span key={idx} className="px-2 py-1 bg-white bg-opacity-70 text-xs rounded border border-gray-200">
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          )}

          {note.metadata.participants && note.metadata.participants.length > 0 && (
            <p className="text-xs text-gray-600 mb-1">
              <span className="font-medium">Participants:</span> {note.metadata.participants.join(', ')}
            </p>
          )}

          {note.metadata.sentiment && (
            <p className="text-xs text-gray-600">
              <span className="font-medium">Sentiment:</span>{' '}
              <span className="capitalize">{note.metadata.sentiment}</span>
            </p>
          )}
        </div>
      )}

      {/* TASK-351: Indicators for attachments, tasks, reminders, and calendar */}
      {(attachmentCount > 0 || taskCount > 0 || hasReminder || hasCalendarEvent) && (
        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-200/50">
          {attachmentCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-600">
              <Paperclip className="w-3 h-3" />
              {attachmentCount} {attachmentCount === 1 ? 'attachment' : 'attachments'}
            </span>
          )}
          {taskCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-600">
              <CheckSquare className="w-3 h-3" />
              {taskCount} {taskCount === 1 ? 'follow-up' : 'follow-ups'}
            </span>
          )}
          {hasReminder && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600">
              <Bell className="w-3 h-3" />
              Reminder set
            </span>
          )}
          {hasCalendarEvent && (
            <span className="inline-flex items-center gap-1 text-xs text-blue-600">
              <Calendar className="w-3 h-3" />
              In calendar
            </span>
          )}
        </div>
      )}

      {/* The follow-ups themselves. Deliberately NOT behind the expand toggle: that button
          only renders for a note whose summary is long enough to truncate, so on a short
          note the count above was unopenable and the RM could never find out what the
          three follow-ups actually were. */}
      {linkedTasks.length > 0 && (
        <ul className="mt-2 space-y-1">
          {linkedTasks.map((task) => (
            <li key={task.id} className="flex items-start gap-2 text-xs text-gray-700">
              <CheckSquare className="mt-0.5 w-3 h-3 flex-shrink-0 text-gray-400" />
              <span className={task.status === 'completed' ? 'line-through text-gray-400' : ''}>
                {task.title}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Client Feedback (TASK-351) */}
      {isExpanded && note.client_feedback && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs font-medium text-gray-700 mb-1">Client Feedback:</p>
          <p className="text-sm text-gray-600 italic">{note.client_feedback}</p>
        </div>
      )}

      {/* Tags */}
      {note.tags && note.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {note.tags.map((tag, idx) => (
            <span key={idx} className="px-2 py-0.5 bg-white bg-opacity-50 text-xs rounded border border-gray-200">
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default NoteCard;
